import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Server, Shield, Zap, Clock, Globe, ArrowRight, CheckCircle, Star, Users, Award, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { getStorageUrl } from '../utils/imageUrlUpdater';

const Home: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Set page title and canonical URL
  useEffect(() => {
    document.title = 'VMon Cloud - Dịch vụ VPS và Hosting chất lượng cao';
    
    // Set canonical URL
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }
    
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = 'https://vmon.vn/';
    document.head.appendChild(canonical);
    
    return () => {
      if (canonical.parentNode) {
        canonical.parentNode.removeChild(canonical);
      }
    };
  }, []);

  const features = [
    {
      icon: Server,
      title: 'PHẦN CỨNG MẠNH MẼ',
      description: 'Khi sử dụng dịch vụ tại VMon Cloud, bạn có thể hoàn toàn yên tâm. Chúng tôi có Hệ thống ổn định được thiết kế dự phòng nhiều lớp, phần cứng được lựa chọn tối ưu. Tính năng giám sát và cảnh báo toàn diện giúp hỗ trợ khách hàng trong mọi tình huống.'
    },
    {
      icon: Zap,
      title: 'KHẢ NĂNG MỞ RỘNG',
      description: 'VMon Cloud cung cấp các giải pháp hoàn toàn có thể mở rộng với giá cả hợp lý. Bạn có thể nâng cấp, tuỳ chỉnh bất kỳ lúc nào khi doanh nghiệp của bạn phát triển. Cloud VPS với tính năng bảo mật tuyệt đối.'
    },
    {
      icon: Shield,
      title: 'THÔNG TIN MINH BẠCH, CHÍNH SÁCH RÕ RÀNG',
      description: 'Tất cả các thông tin liên quan đến điều khoản và chính sách của VMon Cloud có nội dung thể hiện đầy đủ, chi tiết và được công khai. Khách hàng có thể an tâm tuyệt đối.'
    },
    {
      icon: Clock,
      title: 'Hỗ Trợ 24/7',
      description: 'Hệ Thống giám sát hoạt động 24/7 giúp phát hiện nhanh các sự cố, phản hồi nhanh chóng, đảm bảo dịch vụ của khách hàng vận hành xuyên suốt và ổn định.'
    }
  ];

  const vpsServices = [
    {
      flag: getStorageUrl('flag/usa-flag.svg'),
      title: 'Cloud VPS US - VPS Mỹ',
      description: 'Cloud VPS US xây dựng trên nền tảng công nghệ ảo hóa KVM kết hợp với hạ tầng kỹ thuật đạt tiêu chuẩn Quốc tế mang đến sự ổn định rất cao cho hệ thống VPS US của VMon Cloud.',
      link: '/vps-us'
    },
    {
      flag: getStorageUrl('flag/vietnam-flag.svg'),
      title: 'Cloud VPS Việt Nam',
      description: 'Cloud VPS Việt Nam được VMon Cloud triển khai trên công nghệ ảo hóa KVM tiên tiến nhất và thời gian khởi tạo nhanh.',
      link: '/vps-viet-nam'
    },
    {
      flag: getStorageUrl('flag/singapore-flag.svg'),
      title: 'Cloud VPS Singapore',
      description: 'Cloud VPS Singapore có Data Center đặt tại thủ đô Singapore. Hiệu suất vượt trội, tốc độ mạng Internet được xếp hạng nhanh nhất thế giới.',
      link: '/vps-singapore'
    },
    {
      flag: getStorageUrl('flag/eu-flag.svg'),
      title: 'Cloud VPS EU - VPS Châu Âu',
      description: 'Cloud VPS Châu Âu có Datacenter đặt tại các nước Châu Âu, nhiều ưu điểm về hạ tầng phần cứng và tốc độ mạng.',
      link: '/vps-chau-au'
    },
    {
      flag: getStorageUrl('flag/canada-flag.svg'),
      title: 'Cloud VPS Canada',
      description: 'VPS Canada có máy chủ với hệ thống RAID phần cứng đảm bảo an toàn cho dữ liệu của bạn.',
      link: '/vps-canada'
    },
    {
      flag: getStorageUrl('flag/australia-flag.svg'),
      title: 'Cloud VPS Australia - VPS Úc',
      description: 'VPS Australia có máy chủ được đặt an toàn tại một trong những cơ sở hoạt động hiệu quả cao của Sydney do APDC sở hữu.',
      link: '/vps-australia'
    }
  ];

  const additionalServices = [
    {
      icon: Server,
      title: 'Dịch vụ Thuê Máy Chủ Vật Lý',
      description: 'Dịch vụ Thuê Server tại VMon Cloud có Phần cứng máy chủ mạnh mẽ, tốc độ mạng nhanh, hiệu năng cao, hỗ trợ 24/7.',
      link: '/thue-may-chu'
    },
    {
      icon: TrendingUp,
      title: 'VPS Windows',
      description: 'VPS chạy trên hệ điều hành Windows. Hỗ trợ đa dạng hệ điều hành Windows Server 2012R2, Windows Server 2019, Windows Server 2022, Windows 10.',
      link: '/vps-windows'
    }
  ];

  const operatingSystems = [
    { name: 'Windows Server 2012', logo: getStorageUrl('logo/windows.svg') },
    { name: 'Windows Server 2016', logo: getStorageUrl('logo/windows.svg') },
    { name: 'Windows Server 2019', logo: getStorageUrl('logo/windows.svg') },
    { name: 'Windows Server 2022', logo: getStorageUrl('logo/windows.svg') },
    { name: 'Windows 10', logo: getStorageUrl('logo/windows.svg') },
    { name: 'Ubuntu', logo: getStorageUrl('logo/ubuntulogo.svg') },
    { name: 'CentOS', logo: getStorageUrl('logo/centos_color_logo.svg') },
    { name: 'AlmaLinux', logo: getStorageUrl('logo/almalinux_icon_logo.svg') },
    { name: 'Rocky Linux', logo: getStorageUrl('logo/rocky_linux_logo.svg') }
  ];

  const testimonials = [
    getStorageUrl('feedback/feedback_1.jpg'),
    getStorageUrl('feedback/feedback_2.jpg'),
    getStorageUrl('feedback/feedback_3.jpg'),
    getStorageUrl('feedback/feedback_4.jpg'),
    getStorageUrl('feedback/feedback_5.jpg'),
    getStorageUrl('feedback/feedback12.jpg'),
    getStorageUrl('feedback/feedback16.jpg'),
    getStorageUrl('feedback/feedback17.jpg')
  ];

  const faqItems = [
    {
      question: 'Khi sử dụng dịch vụ tại VMon Cloud sẽ hỗ trợ kỹ thuật cho tôi như thế nào?',
      answer: 'Khi sử dụng dịch vụ tại VMon Cloud, bạn có thể hoàn toàn yên tâm. Chúng tôi có đội ngũ tư vấn và hỗ trợ kỹ thuật chuyên nghiệp giúp khách hàng giải quyết được các vấn đề phát sinh một cách nhanh gọn nhất, không làm ảnh hưởng đến hiệu suất công việc. Đội ngũ nhân viên của chúng tôi với kinh nghiệm và trình độ chuyên môn cao sẵn sàng hỗ trợ khách hàng 24/7, mang đến cho khách hàng trải nghiệm tốt nhất khi sử dụng dịch vụ tại VMon Cloud.'
    },
    {
      question: 'Chính sách hoàn tiền của VMon Cloud như thế nào?',
      answer: 'Khi sử dụng dịch vụ tại VMon Cloud, chúng tôi hỗ trợ việc hoàn tiền trong vòng 30 ngày kể từ khi dịch vụ của quý khách được kích hoạt trên hệ thống (không tính thời gian dùng thử dịch vụ). Quý khách có thể xem nội dung chi tiết tại điều khoản sử dụng.'
    },
    {
      question: 'VMon Cloud cung cấp những dịch vụ gì?',
      answer: 'Chúng tôi cung cấp các dịch vụ và sản phẩm công nghệ điện toán đám mây như: VPS, Server, Firewall Anti DDoS…'
    },
    {
      question: 'VMon Cloud có đảm bảo bảo mật an toàn các thông tin cá nhân và dữ liệu của tôi không?',
      answer: 'Đảm bảo an toàn thông tin cá nhân của khách hàng luôn là yêu cầu hàng đầu mà chúng tôi đặt ra. Bạn có thể hoàn toàn yên tâm, chúng tôi sẽ không xâm phạm thông tin và dữ liệu của khách hàng khi chưa được cho phép. Điều này được thể hiện chi tiết, minh bạch trong Điều khoản của chúng tôi.'
    },
    {
      question: 'Tôi có thể liên hệ với VMon Cloud bằng cách nào?',
      answer: 'Hiện tại, bạn có thể kết nối với chúng tôi qua những kênh chat phổ biến như Skype, Telegram, Zalo, Messenger. Ngoài ra trong các trường hợp cần hỗ trợ nhanh chóng, bạn có thể gọi đến hotline: 0966.805.315 để được giải quyết nhanh nhất. Mọi thông tin bạn có thể xem chi tiết tại trang liên hệ.'
    },
    {
      question: 'Tôi muốn nâng cấp dịch vụ thì bằng cách nào?',
      answer: 'Khi có nhu cầu nâng cấp dịch vụ, Quý khách có thể tiến hành nâng cấp trực tiếp một cách nhanh chóng thông qua trang quản trị dịch vụ của VMon Cloud. Số tiền và thời gian sử dụng tương ứng sẽ được hệ thống tự động tính toán và thông báo đến bạn.'
    },
    {
      question: 'Tôi lần đầu sử dụng Cloud VPS thì cách sử dụng như thế nào?',
      answer: 'VMon Cloud có đội ngũ nhân viên luôn sẵn lòng hỗ trợ bạn. Bạn cũng có thể xem các bài viết, video hướng dẫn rất chi tiết về cách sử dụng VPS Windows.'
    }
  ];

  const datacenters = [
    { country: 'Mỹ', locations: '10 Locations', flag: getStorageUrl('flag/usa-flag.svg') },
    { country: 'Đức', locations: '5 Locations', flag: getStorageUrl('flag/germany-flag.svg') },
    { country: 'Pháp', locations: '8 Locations', flag: getStorageUrl('flag/france-flag.svg') },
    { country: 'Singapore', locations: '12 Locations', flag: getStorageUrl('flag/singapore-flag.svg') },
    { country: 'Vương Quốc Anh', locations: '12 Locations', flag: getStorageUrl('flag/uk-flag.svg') },
    { country: 'Việt Nam', locations: '10 Locations', flag: getStorageUrl('flag/vietnam-flag.svg') },
    { country: 'Australia', locations: '5 Locations', flag: getStorageUrl('flag/australia-flag.svg') },
    { country: 'Canada', locations: '8 Locations', flag: getStorageUrl('flag/canada-flag.svg') },
    { country: 'Poland', locations: '12 Locations', flag: getStorageUrl('flag/poland-flag.svg') },
    { country: 'Tương lai', locations: 'Còn mở rộng nữa', flag: getStorageUrl('flag/comingsoon.svg') }
  ];

  // Auto-slide functionality
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                VMon Cloud
              </h1>
              <p className="text-xl mb-8">
                Cloud VPS chất lượng, sử dụng phần cứng mạnh mẽ 100% SSD NVMe và Dịch vụ Khách hàng 24/7. Chúng tôi luôn ở đây đồng hành cùng bạn!
              </p>
            </div>
            <div className="flex justify-center">
              <div className="w-96 h-96 bg-blue-500 bg-opacity-30 rounded-full flex items-center justify-center">
                <Server className="h-48 w-48 text-white opacity-80" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Dịch vụ tiêu biểu tại VMon Cloud
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Dịch vụ Cloud VPS, hiệu suất cao, đồng hành cùng sự phát triển của bạn.
            </p>
          </div>

          {/* VPS Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {vpsServices.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex justify-center mb-4">
                  <img
                    src={service.flag}
                    alt={service.title}
                    className="w-16 h-12 object-contain rounded"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                  <Link to={service.link} className="hover:text-blue-600">
                    {service.title}
                  </Link>
                </h3>
                <p className="text-gray-600 text-center">{service.description}</p>
              </div>
            ))}
          </div>

          {/* Additional Services */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {additionalServices.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <service.icon className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                  <Link to={service.link} className="hover:text-blue-600">
                    {service.title}
                  </Link>
                </h3>
                <p className="text-gray-600 text-center">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-8">
                Cloud VPS Của VMon Cloud Có Gì Tốt?
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {features.map((feature, index) => (
                  <div key={index} className="bg-blue-500 bg-opacity-50 rounded-lg p-6">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mb-4">
                      <feature.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold mb-3">{feature.title}</h3>
                    <p className="text-blue-100 text-sm">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center">
              <div className="w-96 h-96 bg-blue-500 bg-opacity-30 rounded-full flex items-center justify-center">
                <Server className="h-48 w-48 text-white opacity-80" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Operating Systems Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Hệ Điều Hành Hỗ Trợ</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Chúng tôi hỗ trợ đầy đủ loại hệ điều Windows và Linux cho dịch vụ Cloud VPS.
              Cài đặt nhanh chóng, thuận tiện chỉ với một click chuột.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {operatingSystems.map((os, index) => (
              <div 
                key={index} 
                className="relative bg-gray-50 rounded-lg p-4 hover:shadow-md transition-all duration-300 cursor-pointer overflow-hidden group"
              >
                {/* Overlay effect */}
                <div className="absolute inset-0 bg-blue-600 bg-opacity-90 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out flex items-center justify-center">
                  <span className="text-white font-semibold text-sm text-center">{os.name}</span>
                </div>
                
                {/* Content */}
                <img
                  src={os.logo}
                  alt={os.name}
                  className="w-full h-12 object-contain mb-2"
                />
                <p className="text-sm text-gray-600 text-center">{os.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Professional Slider */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">KHÁCH HÀNG NÓI GÌ VỀ CHÚNG TÔI?</h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              Tại VMon Cloud, ý kiến của khách hàng được đặt lên hàng đầu và chúng tôi luôn sẵn sàng lắng nghe và tiếp thu phản hồi để không ngừng cải thiện chất lượng sản phẩm và dịch vụ. Điều này giúp chúng tôi đáp ứng tốt hơn nhu cầu và mong muốn của khách hàng một cách tối đa.
            </p>
          </div>

          {/* Slider Container */}
          <div className="relative max-w-4xl mx-auto">
            {/* Main Slider */}
            <div className="relative overflow-hidden rounded-lg shadow-lg bg-white">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="w-full flex-shrink-0 flex justify-center items-center p-4">
                    <img
                      src={testimonial}
                      alt={`Customer Feedback ${index + 1}`}
                      className="max-w-full max-h-96 object-contain rounded-lg shadow-sm"
                    />
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-3 shadow-lg transition-all"
              >
                <ChevronLeft className="h-6 w-6 text-gray-600" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-3 shadow-lg transition-all"
              >
                <ChevronRight className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentSlide ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Câu Hỏi Thường Gặp</h2>
            <p className="text-lg text-gray-600">
              Chúng tôi đã biên soạn một danh sách các câu trả lời cho các câu hỏi phổ biến.
            </p>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <details key={index} className="bg-gray-50 rounded-lg p-6">
                <summary className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600">
                  {item.question}
                </summary>
                <p className="mt-4 text-gray-600">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Datacenter Locations Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Danh Sách Trung Tâm Máy Chủ</h2>
            <p className="text-lg">Datacenter Locations</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {datacenters.map((datacenter, index) => (
              <div key={index} className="bg-blue-500 bg-opacity-50 rounded-lg p-4 text-center">
                <img
                  src={datacenter.flag}
                  alt={datacenter.country}
                  className="w-12 h-8 object-contain rounded mx-auto mb-3"
                />
                <h3 className="font-semibold mb-1">{datacenter.country}</h3>
                <p className="text-sm text-blue-200">{datacenter.locations}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Cùng nhau, Trực tuyến.
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Chúng tôi ở đây giúp các bạn có thể làm việc online một cách hiệu quả nhất.
          </p>
          <Link
            to="/vps-viet-nam"
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors inline-flex items-center"
          >
            Bắt đầu ngay <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;